/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  getDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../firebase/config";
import toast from "react-hot-toast";

const useBookAssignments = (bookId) => {
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch users assigned to this book
  const fetchAssignedUsers = async () => {
    if (!bookId) return;

    setLoading(true);
    setError(null);

    try {
      // Try to get from bookAssignments collection first (more efficient)
      let assignedUsersData = [];

      try {
        const bookAssignmentsQuery = query(
          collection(db, "bookAssignments"),
          where("bookId", "==", bookId),
          where("status", "==", "active"),
          orderBy("assignedAt", "desc"),
          limit(100) // Limit to prevent excessive reads
        );

        const assignmentsSnapshot = await getDocs(bookAssignmentsQuery);

        // Get user data for assigned users
        const userIds = assignmentsSnapshot.docs.map(
          (doc) => doc.data().userId
        );

        if (userIds.length > 0) {
          // Get user data in batches to avoid excessive reads
          const batchSize = 10;
          for (let i = 0; i < userIds.length; i += batchSize) {
            const batch = userIds.slice(i, i + batchSize);

            const userPromises = batch.map(async (userId) => {
              try {
                const userDoc = await getDoc(doc(db, "users", userId));
                if (userDoc.exists()) {
                  return {
                    userId: userId,
                    userData: userDoc.data(),
                    assignmentData: {
                      bookid: bookId,
                      timestamp: assignmentsSnapshot.docs
                        .find((doc) => doc.data().userId === userId)
                        ?.data()?.assignedAt,
                    },
                  };
                }
              } catch (err) {
                console.log(`Error fetching user ${userId}:`, err);
              }
              return null;
            });

            const batchResults = await Promise.all(userPromises);
            assignedUsersData.push(...batchResults.filter(Boolean));

            // Small delay between batches
            if (i + batchSize < userIds.length) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
        }
      } catch (err) {
        // Fallback to old method but with limits
        console.log(
          "bookAssignments collection not found, using fallback method"
        );

        // Get only active users (limit to 100 instead of ALL users)
        const usersQuery = query(
          collection(db, "users"),
          where("status", "==", "active"),
          limit(100)
        );

        const usersSnapshot = await getDocs(usersQuery);
        const users = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Check which users have this book assigned in their Mybooks collection
        for (const user of users) {
          try {
            const userBooksRef = collection(db, "Mybooks", user.id, "books");
            const bookDoc = await getDocs(
              query(userBooksRef, where("bookid", "==", bookId))
            );

            if (!bookDoc.empty) {
              const bookData = bookDoc.docs[0].data();
              assignedUsersData.push({
                userId: user.id,
                userData: user,
                assignmentData: {
                  bookid: bookData.bookid,
                  timestamp: bookData.timestamp,
                },
              });
            }
          } catch (err) {
            console.log(`No books subcollection for user ${user.id}`);
          }
        }
      }

      setAssignedUsers(assignedUsersData);
    } catch (err) {
      console.error("Error fetching assigned users:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Assign users to this book
  const assignUsers = async (userIds) => {
    if (!bookId || userIds.length === 0) return;

    setOperationLoading(true);
    setError(null);

    try {
      const batch = writeBatch(db);

      for (const userId of userIds) {
        // Create document in Mybooks/{userId}/books/{bookId}
        const bookAssignmentRef = doc(db, "Mybooks", userId, "books", bookId);
        batch.set(bookAssignmentRef, {
          bookid: bookId,
          timestamp: serverTimestamp(),
        });
      }

      await batch.commit();

      // Show success toast
      toast.success(
        `Successfully assigned ${userIds.length} user${
          userIds.length > 1 ? "s" : ""
        } to the book!`
      );

      // Refresh the assigned users list
      await fetchAssignedUsers();

      return { success: true };
    } catch (err) {
      console.error("Error assigning users:", err);
      setError(err.message);
      toast.error(`Failed to assign users: ${err.message}`);
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Remove users from this book
  const removeUsers = async (userIds) => {
    if (!bookId || userIds.length === 0) return;

    setOperationLoading(true);
    setError(null);

    try {
      const batch = writeBatch(db);

      for (const userId of userIds) {
        // Delete document from Mybooks/{userId}/books/{bookId}
        const bookAssignmentRef = doc(db, "Mybooks", userId, "books", bookId);
        batch.delete(bookAssignmentRef);
      }

      await batch.commit();

      // Show success toast
      toast.success(
        `Successfully removed ${userIds.length} user${
          userIds.length > 1 ? "s" : ""
        } from the book!`
      );

      // Refresh the assigned users list
      await fetchAssignedUsers();

      return { success: true };
    } catch (err) {
      console.error("Error removing users:", err);
      setError(err.message);
      toast.error(`Failed to remove users: ${err.message}`);
      return { success: false, error: err.message };
    } finally {
      setOperationLoading(false);
    }
  };

  // Check if a specific user is assigned to this book
  const isUserAssigned = (userId) => {
    return assignedUsers.some((assignment) => assignment.userId === userId);
  };

  // Get the count of users having this book
  const getUserCount = async () => {
    if (!bookId) return 0;

    try {
      // Get all users with limits
      const usersQuery = query(collection(db, "users"), limit(100));
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      let userCount = 0;

      // Check which users have this book assigned in their Mybooks collection
      for (const user of users) {
        try {
          const userBooksRef = collection(db, "Mybooks", user.id, "books");
          const bookDoc = await getDocs(
            query(userBooksRef, where("bookid", "==", bookId))
          );

          if (!bookDoc.empty) {
            userCount++;
          }
        } catch (err) {
          // Skip users with no books subcollection
          continue;
        }
      }

      return userCount;
    } catch (err) {
      console.error("Error getting user count:", err);
      return 0;
    }
  };

  // Get counts for all books
  const getAllBookUserCounts = async () => {
    try {
      // Get all users with limits
      const usersQuery = query(collection(db, "users"), limit(100));
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const bookUserCounts = {};

      // For each user, get their books and count them
      for (const user of users) {
        try {
          const userBooksRef = collection(db, "Mybooks", user.id, "books");
          const userBooksSnapshot = await getDocs(userBooksRef);

          userBooksSnapshot.docs.forEach((bookDoc) => {
            const bookData = bookDoc.data();
            const bookId = bookData.bookid;

            if (bookId) {
              bookUserCounts[bookId] = (bookUserCounts[bookId] || 0) + 1;
            }
          });
        } catch (err) {
          // Skip users with no books subcollection
          continue;
        }
      }

      return bookUserCounts;
    } catch (err) {
      console.error("Error getting all book user counts:", err);
      return {};
    }
  };

  useEffect(() => {
    if (bookId) {
      fetchAssignedUsers();
    }
  }, [bookId]);

  return {
    assignedUsers,
    loading,
    operationLoading,
    error,
    assignUsers,
    removeUsers,
    isUserAssigned,
    getUserCount,
    getAllBookUserCounts,
    refreshAssignedUsers: fetchAssignedUsers,
  };
};

export default useBookAssignments;
